import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Chip,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { getPatientProfileRequest, deletePatientProfileRequest, resetDelete, resetPatientData } from '../../../redux/modules/admin/patientProfile/slice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DeleteConfirmationModal } from '../../../utils/modal';
import { usePermissions } from '../../../hooks/usePermissions';
import PatientTabHeader from './patientTabHeader';
import moment from 'moment';
import { DATE_FORMAT } from '../../../helper/dateTime';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    padding: '14px 16px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiTableCell-body': {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const GenderChip = styled(Chip)(({ theme, gendertype }) => {
  const colors = {
    male: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' },
    female: { bg: 'rgba(236, 72, 153, 0.1)', color: '#EC4899' },
    other: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' },
  };
  const colorSet = colors[gendertype?.toLowerCase()] || colors.other;
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
    textTransform: 'capitalize',
  };
});

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  marginRight: 4,
  backgroundColor: color === 'error'
    ? 'rgba(239, 68, 68, 0.08)'
    : 'rgba(105, 203, 249, 0.08)',
  '&:hover': {
    backgroundColor: color === 'error'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(105, 203, 249, 0.15)',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  color: theme.palette.text.secondary,
}));

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  '& .MuiPagination-ul': {
    '& .MuiPaginationItem-root': {
      borderRadius: 8,
      fontWeight: 500,
    },
  },
}));

const tableHeading = ["Full Name", "Patient ID", "Email Address", "Phone Number", "Date of Birth", "Gender", "Action"];

const formatPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('592') && digits.length === 10) {
    return `+592 ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return `+${digits}`;
};

let deleteItemId;

export default function PatientList({ setItemData, toggleList }) {
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("");
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role } = useParams();
  const { canWrite } = usePermissions();
  const writeAllowed = canWrite('patients');
  let { getInfoRes, deleteRes, loading } = useSelector(state => state.patientProfile);
  let { currentBranch } = useSelector(state => state.branch);
  const [isModalOpen, setIsModalOpen] = useState(false);
  let cardLimit = 10;
  let pageCount = 0;

  if (getInfoRes?.dataCount && getInfoRes?.dataCount > cardLimit) {
    pageCount = getInfoRes.dataCount / cardLimit;
    if (pageCount % 2 !== 0) {
      pageCount = Math.ceil(pageCount);
    }
  }

  useEffect(() => {
    if (!currentBranch) { return; }
    dispatch(getPatientProfileRequest({
      limit: cardLimit, search: searchText, searchType: searchType, skip: (page - 1) * cardLimit,
      currentBranch: currentBranch?.id,
    }));
    dispatch(resetPatientData());
    localStorage.removeItem("pId");
  }, [dispatch, currentBranch, searchText, searchType, cardLimit, page]);

  useEffect(() => {
    if (!deleteRes) { return; }
    if (deleteRes?.success) {
      dispatch(getPatientProfileRequest({
        limit: cardLimit, search: searchText, searchType: searchType, skip: (page - 1) * cardLimit,
        currentBranch: currentBranch?.id,
      }));
    }
    toast(deleteRes?.message);
    dispatch(resetDelete());
  }, [dispatch, deleteRes, currentBranch, cardLimit, page, searchText, searchType]);

  const handleSearchCallback = (type, text) => {
    setSearchType(type);
    setSearchText(text);
    setPage(1);
  };

  const handlePagination = (event, value) => {
    setPage(value);
  };

  // Loading State
  const renderSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          {tableHeading.map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <>
      <PatientTabHeader handleSearchCallback={handleSearchCallback} />

      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <StyledTableContainer component={Paper}>
        <Table sx={{ minWidth: { xs: 600, md: 700 } }} aria-label="patient table">
          <StyledTableHead>
            <TableRow>
              {tableHeading.map(elem => (
                <TableCell key={elem}>{elem}</TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <StyledTableBody>
            {loading ? (
              renderSkeleton()
            ) : getInfoRes?.data?.length > 0 ? (
              getInfoRes.data.map(elem => (
                <TableRow key={elem._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: 'primary.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.dark',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        {elem.fName?.[0]?.toUpperCase()}{elem.lName?.[0]?.toUpperCase()}
                      </Box>
                      <Typography fontWeight={500}>
                        {elem.fName} {elem.lName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {elem?.mrn || elem?.orthancPatientId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {elem?.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatPhone(elem?.contactNumber)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {elem?.dob ? moment.utc(elem?.dob).format(DATE_FORMAT) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {elem?.gender && (
                      <GenderChip
                        label={elem.gender}
                        gendertype={elem.gender}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Encounters">
                        <ActionButton
                          size="small"
                          onClick={() => navigate(`/${role || 'admin'}/patients/${elem._id}/encounters`)}
                          sx={{ backgroundColor: 'rgba(5,47,93,0.08)', '&:hover': { backgroundColor: 'rgba(5,47,93,0.15)' } }}
                        >
                          <AssignmentIndIcon fontSize="small" sx={{ color: '#052F5D' }} />
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title={writeAllowed ? "Edit Patient" : "You have read-only access to patients."}>
                        <span>
                          <ActionButton
                            size="small"
                            disabled={!writeAllowed}
                            onClick={() => { setItemData(elem); toggleList(); }}
                          >
                            <EditIcon fontSize="small" sx={{ color: writeAllowed ? 'primary.main' : 'text.disabled' }} />
                          </ActionButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={writeAllowed ? "Delete Patient" : "You have read-only access to patients."}>
                        <span>
                          <ActionButton
                            size="small"
                            color="error"
                            disabled={!writeAllowed}
                            onClick={() => { deleteItemId = elem._id; setIsModalOpen(true); }}
                          >
                            <DeleteIcon fontSize="small" sx={{ color: writeAllowed ? 'error.main' : 'text.disabled' }} />
                          </ActionButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeading.length}>
                  <EmptyState>
                    <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" fontWeight={500} color="text.secondary">
                      No Patients Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchText ? 'Try adjusting your search criteria' : 'Add patients to get started'}
                    </Typography>
                  </EmptyState>
                </TableCell>
              </TableRow>
            )}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
      </Box>

      {pageCount > 1 && (
        <PaginationWrapper>
          <Pagination
            count={pageCount}
            color="primary"
            shape="rounded"
            page={page}
            onChange={handlePagination}
          />
        </PaginationWrapper>
      )}

      <DeleteConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => { dispatch(deletePatientProfileRequest(deleteItemId)); setIsModalOpen(false); }}
      />
    </>
  );
}
